import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { getAircraftOnGround, getParkingStands, updateAircraftParking, checkStandCompatibility, getParkingConfig, getAircraftRotation, updateFlight, getHistoricalParking, getFlightDates, getStandClosures, createManualAircraft, deleteManualAircraft, lookupAircraft, updateManualAircraftStand, updateManualAircraft } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { 
  Plane, Calendar as CalendarIcon, RefreshCw, Loader2, 
  AlertTriangle, Clock, MapPin, MoveHorizontal, RotateCw,
  ZoomIn, ZoomOut, Maximize2, Info, PlaneLanding, PlaneTakeoff, Settings,
  ArrowRight, Edit2, Save, X, History, Ban, CalendarClock, Sliders, Trash2, Plus, Search
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { usePermissions } from "@/context/PermissionContext";
import { useAuth } from "@/context/AuthContext";
import StandClosureScheduler from "@/components/StandClosureScheduler";
import OperationalRestrictionScheduler from "@/components/OperationalRestrictionScheduler";
import apronChartDefault from "@/assets/apron_chart.jpg";

// Default apron chart image (bundled locally so it always loads in every environment)
const DEFAULT_APRON_IMAGE_URL = apronChartDefault;

// Aircraft shape definitions matching the apron areas
const AIRCRAFT_SHAPES = {
  // P1 Apron - Turboprop with T-tail (stands 1-4 A/B)
  "P1": {
    name: "P1 - Turboprop",
    path: "M20 1 L23 12 L38 18 L38 22 L23 20 L23 30 L18 30 L18 34 L14 34 L14 38 L20 36 L26 38 L26 34 L22 34 L22 30 L17 30 L17 20 L2 22 L2 18 L17 12 Z",
    viewBox: "0 0 40 40"
  },
  // P2 Apron - Light jet with T-tail
  "P2": {
    name: "P2 - Light Jet", 
    path: "M20 2 L23 14 L36 18 L36 22 L23 20 L23 28 L19 28 L19 32 L15 32 L15 36 L20 34 L25 36 L25 32 L21 32 L21 28 L17 28 L17 20 L4 22 L4 18 L17 14 Z",
    viewBox: "0 0 40 40"
  },
  // P3 Apron - Business jet with rear engines (stands 17-18 B/C)
  "P3": {
    name: "P3 - Business Jet",
    path: "M20 1 L22 14 L38 22 L37 26 L22 22 L22 26 L18 26 L18 28 L15 28 L15 32 L18 32 L18 36 L20 34 L22 36 L22 32 L25 32 L25 28 L22 28 L22 26 L18 22 L3 26 L2 22 L18 14 Z",
    viewBox: "0 0 40 40"
  },
  // P5 Apron - Airliner with underwing engines (stands 30-39)
  "P5": {
    name: "P5 - Airliner",
    path: "M20 1 L21 4 C22 4 23 5 23 6 L23 14 L38 22 L36 26 L23 20 L23 30 L17 34 L17 38 L20 36 L23 38 L23 34 L17 30 L17 20 L4 26 L2 22 L17 14 L17 6 C17 5 18 4 19 4 Z M12 18 L12 24 L8 24 L7 22 L8 20 Z M28 18 L28 24 L32 24 L33 22 L32 20 Z",
    viewBox: "0 0 40 40"
  },
  // Default - Generic jet shape
  "DEFAULT": {
    name: "Default",
    path: "M20 2 L22 14 L36 18 L36 22 L22 20 L22 32 L16 36 L16 38 L20 36 L24 38 L24 36 L18 32 L18 20 L4 22 L4 18 L18 14 Z",
    viewBox: "0 0 40 40"
  },
  // Helicopter
  "HELI": {
    name: "Helicopter",
    path: "M20 12 C25 12 28 15 28 19 C28 23 25 26 20 26 C15 26 12 23 12 19 C12 15 15 12 20 12 Z M3 17 L37 21 M7 13 L33 25 M20 26 L20 30 L30 30 L32 28 L34 30 L32 32 L20 32 Z",
    viewBox: "0 0 40 40"
  }
};

// Function to get default shape based on stand ID
const getDefaultShapeForStand = (standId) => {
  if (!standId) return "DEFAULT";
  const id = standId.toUpperCase();
  if (/^[1-4][AB]$/.test(id)) return "P1";
  if (/^1[78][BC]$/.test(id)) return "P3";
  if (/^3[0-9]$/.test(id)) return "P5";
  return "P2";
};

// Default fallback positions (used if no config exists in database)
const DEFAULT_STAND_POSITIONS = {
  // P1 stands - Based on SUD3.jpg map (7024 x 2620 pixels)
  // Coordinates scaled from image analysis (scale factor ~4.5)
  // A stands face UP (0), B stands face DOWN (180)
  "1A": { x: 220, y: 1427, rotation: 0, apron: "P1", scale: 1 },
  "1B": { x: 463, y: 1598, rotation: 180, apron: "P1", scale: 1 },
  "2A": { x: 707, y: 1424, rotation: 0, apron: "P1", scale: 1 },
  "2B": { x: 954, y: 1598, rotation: 180, apron: "P1", scale: 1 },
  "3A": { x: 1215, y: 1424, rotation: 0, apron: "P1", scale: 1 },
  "3B": { x: 1467, y: 1598, rotation: 180, apron: "P1", scale: 1 },
  "4A": { x: 1733, y: 1424, rotation: 0, apron: "P1", scale: 1 },
  "4B": { x: 1976, y: 1602, rotation: 180, apron: "P1", scale: 1 },
  // P3 stands - 17B, 17C, 18B, 18C
  "17B": { x: 2169, y: 1953, rotation: -45, apron: "P3", scale: 1 },
  "18B": { x: 2417, y: 2133, rotation: 45, apron: "P3", scale: 1 },
  "17C": { x: 2687, y: 1949, rotation: -45, apron: "P3", scale: 1 },
  "18C": { x: 2930, y: 2133, rotation: 45, apron: "P3", scale: 1 },
  // P4 stands - removed 19-27 (do not exist physically)
  // P5 stands - 30-39 (facing down, towards S4 taxiway)
  "30": { x: 4253, y: 2354, rotation: 180, apron: "P5", scale: 1 },
  "31": { x: 4541, y: 2354, rotation: 180, apron: "P5", scale: 1 },
  "32": { x: 4824, y: 2354, rotation: 180, apron: "P5", scale: 1 },
  "33": { x: 5108, y: 2354, rotation: 180, apron: "P5", scale: 1 },
  "34": { x: 5387, y: 2354, rotation: 180, apron: "P5", scale: 1 },
  "36": { x: 5954, y: 2354, rotation: 180, apron: "P5", scale: 1 },
  "37": { x: 6237, y: 2354, rotation: 180, apron: "P5", scale: 1 },
  "38": { x: 6520, y: 2354, rotation: 180, apron: "P5", scale: 1 },
  "39": { x: 6800, y: 2354, rotation: 180, apron: "P5", scale: 1 },
};

// Stands that should show visual indicators (circles, labels) on the map
// P2 stands and special stands outside the numbered aprons need visual circles
// P1, P3, P4, P5 stands already have printed labels on the map image
const VISUAL_INDICATOR_STANDS = new Set([
  "P2/1", "P2/2", "P2/3", "P2/4",
  "S10", "CICADE", "CICADE2", "S15", "S16", "S17"
]);

const shouldShowVisualIndicator = (standId, pos) => {
  if (VISUAL_INDICATOR_STANDS.has(standId)) return true;
  // Respect per-stand config from database
  if (pos.show_visual === true) return true;
  if (pos.show_visual === false) return false;
  // Default: only show for non-numbered aprons (P2, special)
  const apron = pos.apron || "";
  return !["P1", "P3", "P4", "P5"].includes(apron);
};

// Color mapping for departure status
const STATUS_COLORS = {
  green: { bg: "#22c55e", label: "Departing Today" },
  orange: { bg: "#f97316", label: "Departing Tomorrow" },
  blue: { bg: "#3b82f6", label: "Departing This Week" },
  gray: { bg: "#6b7280", label: "Departing Later" },
  black: { bg: "#1f2937", label: "Long Term Parking" },
  purple: { bg: "#8b5cf6", label: "Manual Entry" },
};

export default function ApronManagementPage() {
  const { isMasterAdmin, isAdmin } = usePermissions();
  const { settings } = useAuth();
  const [loading, setLoading] = useState(true);
  const [aircraftOnGround, setAircraftOnGround] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [flightDates, setFlightDates] = useState([]);
  const [stands, setStands] = useState({});
  const [standPositions, setStandPositions] = useState(DEFAULT_STAND_POSITIONS);
  const [standClosures, setStandClosures] = useState([]);
  // Default zoom to fit image in window - adjusted for new SUD3 map
  const [zoom, setZoom] = useState(0.24);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const [hoveredAircraft, setHoveredAircraft] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [draggedAircraft, setDraggedAircraft] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [warningData, setWarningData] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [isHistoricalMode, setIsHistoricalMode] = useState(false);
  const [historicalData, setHistoricalData] = useState(null);
  // When TODAY is selected, lets staff view today's parking history (including
  // aircraft that already departed today) instead of only currently-on-ground.
  const [showTodayHistory, setShowTodayHistory] = useState(false);
  
  // Get the apron map URL - use custom map from settings if available, otherwise default
  const apronMapUrl = settings?.apron_map_base64 || DEFAULT_APRON_IMAGE_URL;
  
  // Get map transform from settings
  const mapTransform = {
    scale: settings?.apron_map_scale || 1.0,
    offsetX: settings?.apron_map_offset_x || 0,
    offsetY: settings?.apron_map_offset_y || 0
  };
  
  // Rotation dialog state
  const [showRotationDialog, setShowRotationDialog] = useState(false);
  const [rotationData, setRotationData] = useState(null);
  const [rotationLoading, setRotationLoading] = useState(false);
  const [editingFlightId, setEditingFlightId] = useState(null);
  const [editingParking, setEditingParking] = useState("");
  const [savingParking, setSavingParking] = useState(false);
  
  // Card parking edit state
  const [editingCardParking, setEditingCardParking] = useState(null); // registration being edited
  const [cardParkingValue, setCardParkingValue] = useState("");
  const [savingCardParking, setSavingCardParking] = useState(false);
  
  // Image size state for fit-to-screen
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  
  // Admin display config mode
  const [showDisplayConfig, setShowDisplayConfig] = useState(false);
  const [displayConfig, setDisplayConfig] = useState({
    aircraftScale: 1.0,
    signScale: 1.0,
    defaultZoom: 0.24,
    showAllPositions: false
  });
  const [savingDisplayConfig, setSavingDisplayConfig] = useState(false);
  
  // Manual aircraft state
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualReg, setManualReg] = useState("");
  const [manualStand, setManualStand] = useState("");
  const [manualArrival, setManualArrival] = useState("");
  const [manualDeparture, setManualDeparture] = useState("");
  const [manualOperator, setManualOperator] = useState("");
  const [manualAircraftType, setManualAircraftType] = useState("");
  const [manualLookupLoading, setManualLookupLoading] = useState(false);
  const [manual24h, setManual24h] = useState(false);
  const [savingManual, setSavingManual] = useState(false);
  const [deletingManualId, setDeletingManualId] = useState(null);
  const [manualStandOpen, setManualStandOpen] = useState(false);
  // Edit external (manual) aircraft state
  const [editManualAircraft, setEditManualAircraft] = useState(null);
  const [editManualForm, setEditManualForm] = useState({});
  const [savingEditManual, setSavingEditManual] = useState(false);
  
  const containerRef = useRef(null);
  const imageRef = useRef(null);

  // Load saved display config from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('apronDisplayConfig');
    if (saved) {
      try {
        const config = JSON.parse(saved);
        setDisplayConfig(config);
        setZoom(config.defaultZoom || 0.24);
      } catch (e) {
        console.error('Failed to load display config', e);
      }
    }
  }, []);

  // Save display config
  const saveDisplayConfig = () => {
    setSavingDisplayConfig(true);
    localStorage.setItem('apronDisplayConfig', JSON.stringify(displayConfig));
    setZoom(displayConfig.defaultZoom);
    setTimeout(() => {
      setSavingDisplayConfig(false);
      setShowDisplayConfig(false);
      toast.success('Display settings saved!');
    }, 500);
  };

  // Manual aircraft - lookup registration
  const handleManualRegLookup = async (reg) => {
    const cleanReg = reg.toUpperCase().replace(/-/g, "");
    setManualReg(cleanReg);
    if (cleanReg.length < 3) {
      setManualOperator("");
      setManualAircraftType("");
      return;
    }
    setManualLookupLoading(true);
    try {
      const res = await lookupAircraft(cleanReg);
      if (res.data) {
        setManualOperator(res.data.operator || "");
        setManualAircraftType(res.data.aircraft_type || "");
      } else {
        setManualOperator("");
        setManualAircraftType("");
      }
    } catch {
      setManualOperator("");
      setManualAircraftType("");
    } finally {
      setManualLookupLoading(false);
    }
  };

  // Manual aircraft - 24h checkbox toggle
  const handleManual24hToggle = (checked) => {
    setManual24h(checked);
    if (checked) {
      const base = isHistoricalMode ? new Date(selectedDate) : new Date();
      const today5am = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 5, 0, 0);
      const tomorrow5am = new Date(today5am);
      tomorrow5am.setDate(tomorrow5am.getDate() + 1);
      // For "today" mode: if it's before 05:00 now, window is yesterday 05:00 → today 05:00
      if (!isHistoricalMode && base < today5am) {
        today5am.setDate(today5am.getDate() - 1);
        tomorrow5am.setDate(tomorrow5am.getDate() - 1);
      }
      const toLocal = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const h = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        return `${y}-${m}-${day}T${h}:${min}`;
      };
      setManualArrival(toLocal(today5am));
      setManualDeparture(toLocal(tomorrow5am));
    } else {
      setManualArrival("");
      setManualDeparture("");
    }
  };

  // Manual aircraft - submit
  const handleManualSubmit = async () => {
    if (!manualReg || !manualStand) {
      toast.error("Registration and stand are required");
      return;
    }
    
    // Check stand compatibility if aircraft type is known
    const acType = manualAircraftType || "";
    if (acType && acType !== "Unknown") {
      try {
        const result = await checkStandCompatibility(manualStand, acType);
        if (!result.data.compatible) {
          toast.error(`Stand ${manualStand} is not compatible with ${acType}: ${result.data.warnings?.join(", ") || "Aircraft too large"}`);
          return;
        } else if (result.data.warnings?.length > 0) {
          toast.warning(`Warning for ${manualStand}: ${result.data.warnings.join(", ")}`, { duration: 6000 });
        }
      } catch (error) {
        console.error("Compatibility check failed:", error);
      }
    }
    
    setSavingManual(true);
    try {
      const response = await createManualAircraft({
        registration: manualReg,
        stand: manualStand,
        operator: manualOperator,
        aircraft_type: manualAircraftType,
        arrival_time: manualArrival || null,
        departure_time: manualDeparture || null
      });
      // Show parking conflict warning if any
      if (response.data?.parking_warning) {
        toast.warning(response.data.parking_warning, { duration: 8000 });
      }
      toast.success(`Manual aircraft ${manualReg} added to stand ${manualStand}`);
      setManualReg("");
      setManualStand("");
      setManualArrival("");
      setManualDeparture("");
      setManualOperator("");
      setManualAircraftType("");
      setManual24h(false);
      setShowManualForm(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to add manual aircraft");
    } finally {
      setSavingManual(false);
    }
  };

  // Manual aircraft - delete
  const handleManualDelete = async (id, reg) => {
    setDeletingManualId(id);
    try {
      await deleteManualAircraft(id);
      toast.success(`Manual aircraft ${reg} removed`);
      fetchData();
    } catch (error) {
      toast.error("Failed to remove manual aircraft");
    } finally {
      setDeletingManualId(null);
    }
  };

  // Manual aircraft - open edit dialog
  const openEditManual = (aircraft) => {
    const toLocalInput = (v) => {
      if (!v) return "";
      return String(v).replace(" ", "T").slice(0, 16);
    };
    setEditManualAircraft(aircraft);
    setEditManualForm({
      registration: aircraft.registration || "",
      stand: aircraft.parking_position || aircraft.stand || "",
      operator: aircraft.operator || "",
      aircraft_type: aircraft.aircraft_type || "",
      arrival_time: toLocalInput(aircraft.arrival_time_full),
      departure_time: toLocalInput(aircraft.departure_time_full),
    });
  };

  // Manual aircraft - save edit
  const handleSaveEditManual = async () => {
    const id = editManualAircraft?.manual_id;
    if (!id) return;
    setSavingEditManual(true);
    try {
      const res = await updateManualAircraft(id, {
        registration: editManualForm.registration,
        stand: editManualForm.stand,
        operator: editManualForm.operator,
        aircraft_type: editManualForm.aircraft_type,
        arrival_time: editManualForm.arrival_time || null,
        departure_time: editManualForm.departure_time || null,
      });
      if (res.data?.parking_warning) {
        toast.warning(res.data.parking_warning, { duration: 8000 });
      }
      toast.success("External aircraft updated");
      setEditManualAircraft(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update external aircraft");
    } finally {
      setSavingEditManual(false);
    }
  };

  // Handle image load to get dimensions
  const handleImageLoad = () => {
    if (imageRef.current) {
      setImageSize({
        width: imageRef.current.naturalWidth,
        height: imageRef.current.naturalHeight
      });
    }
  };

  // Fit map to screen
  const fitToScreen = () => {
    const container = containerRef.current;
    if (!container || !imageSize.width) return;
    
    const containerHeight = 700; // Match the container height
    const containerWidth = container.clientWidth || 900;
    
    const scaleX = containerWidth / imageSize.width;
    const scaleY = containerHeight / imageSize.height;
    const newZoom = Math.min(scaleX, scaleY) * 0.95;
    
    setZoom(Math.max(0.05, Math.min(newZoom, 1)));
    setPan({ x: 0, y: 0 });
  };

  // Fetch stand closures
  const fetchClosures = useCallback(async () => {
    try {
      const response = await getStandClosures();
      setStandClosures(response.data.closures || []);
    } catch (error) {
      console.error("Failed to fetch closures:", error);
    }
  }, []);

  // Fetch flight dates for calendar highlighting
  useEffect(() => {
    const fetchDates = async () => {
      try {
        const response = await getFlightDates();
        // Convert date strings to Date objects
        const dates = response.data.dates.map(d => parseISO(d));
        setFlightDates(dates);
      } catch (error) {
        console.error("Failed to fetch flight dates:", error);
      }
    };
    fetchDates();
  }, []);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const today = format(new Date(), "yyyy-MM-dd");
      // Historical for past dates, OR for today when the user opts to view
      // today's history (so same-day departed aircraft stay visible).
      const isHistorical = dateStr < today || (dateStr === today && showTodayHistory);
      setIsHistoricalMode(isHistorical);
      
      const [aircraftRes, standsRes, configRes] = await Promise.all([
        getAircraftOnGround(dateStr),
        getParkingStands(),
        getParkingConfig()
      ]);
      
      setAircraftOnGround(aircraftRes.data.aircraft || []);
      setStands(standsRes.data.stands || {});
      
      // Merge database config with defaults - include shape and deactivation info
      const dbPositions = {};
      (configRes.data.stands || []).forEach(config => {
        dbPositions[config.stand_id] = {
          x: config.x,
          y: config.y,
          rotation: config.rotation,
          scale: config.scale || 1,
          shape: config.shape || "DEFAULT",
          apron: config.apron,
          deactivated: config.deactivated || false,
          deactivation_start: config.deactivation_start || "",
          deactivation_end: config.deactivation_end || "",
          deactivation_reason: config.deactivation_reason || "",
          // Restrictions for validation
          max_wingspan: config.max_wingspan || "",
          max_length: config.max_length || "",
          max_height: config.max_height || "",
          tail_wheel_prohibited: config.tail_wheel_prohibited || false
        };
      });
      
      // Use database positions directly - they are configured for the current map
      setStandPositions(prev => ({
        ...DEFAULT_STAND_POSITIONS,
        ...dbPositions
      }));
      
      // If historical mode, also fetch historical parking data
      if (isHistorical) {
        try {
          const histRes = await getHistoricalParking(dateStr);
          setHistoricalData(histRes.data);
        } catch (err) {
          console.error("Error fetching historical data:", err);
          setHistoricalData(null);
        }
      } else {
        setHistoricalData(null);
      }
      
      // Fetch closures
      await fetchClosures();
    } catch (error) {
      console.error("Error fetching apron data:", error);
      toast.error("Failed to load apron data");
    } finally {
      setLoading(false);
    }
  }, [selectedDate, fetchClosures, showTodayHistory]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Open rotation dialog
  const openRotationDialog = async (aircraft) => {
    setShowRotationDialog(true);
    setRotationLoading(true);
    setRotationData(null);
    
    try {
      // Pass the selected date as context so we see the relevant arrival/departure
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const response = await getAircraftRotation(aircraft.registration, dateStr);
      setRotationData(response.data);
    } catch (error) {
      console.error("Error fetching rotation:", error);
      toast.error("Failed to load rotation data");
    } finally {
      setRotationLoading(false);
    }
  };

  // Update parking position for a flight
  const updateFlightParking = async (flightId, newParking, aircraftType) => {
    // Check compatibility before updating
    if (aircraftType && aircraftType !== "Unknown") {
      try {
        const compat = await checkStandCompatibility(newParking, aircraftType);
        if (!compat.data.compatible) {
          toast.error(`Stand ${newParking} is not compatible with ${aircraftType}: ${compat.data.warnings?.join(", ") || "Aircraft too large"}`);
          return;
        } else if (compat.data.warnings?.length > 0) {
          toast.warning(`Warning for ${newParking}: ${compat.data.warnings.join(", ")}`, { duration: 6000 });
        }
      } catch (error) {
        console.error("Compatibility check failed:", error);
      }
    }
    
    setSavingParking(true);
    try {
      await updateFlight(flightId, { parking_position: newParking.toUpperCase() });
      toast.success("Parking position updated");
      setEditingFlightId(null);
      setEditingParking("");
      
      // Refresh rotation data with context date
      if (rotationData) {
        const dateStr = format(selectedDate, "yyyy-MM-dd");
        const response = await getAircraftRotation(rotationData.registration, dateStr);
        setRotationData(response.data);
      }
      
      // Refresh main data
      await fetchData();
    } catch (error) {
      console.error("Error updating parking:", error);
      toast.error("Failed to update parking position");
    } finally {
      setSavingParking(false);
    }
  };

  // Update parking position from card (updates both inbound and outbound flights)
  const updateCardParking = async (registration, newParking, aircraftType) => {
    // Check compatibility before updating
    if (aircraftType && aircraftType !== "Unknown") {
      try {
        const compat = await checkStandCompatibility(newParking, aircraftType);
        if (!compat.data.compatible) {
          toast.error(`Stand ${newParking} is not compatible with ${aircraftType}: ${compat.data.warnings?.join(", ") || "Aircraft too large"}`);
          return;
        } else if (compat.data.warnings?.length > 0) {
          toast.warning(`Warning for ${newParking}: ${compat.data.warnings.join(", ")}`, { duration: 6000 });
        }
      } catch (error) {
        console.error("Compatibility check failed:", error);
      }
    }
    
    setSavingCardParking(true);
    try {
      // Get the rotation data to find both flights - pass context date
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const rotRes = await getAircraftRotation(registration, dateStr);
      const rotation = rotRes.data;
      
      // Update last arrival if exists
      if (rotation.last_arrival) {
        await updateFlight(rotation.last_arrival.id, { parking_position: newParking.toUpperCase() });
      }
      
      // Update last departure if exists
      if (rotation.last_departure) {
        await updateFlight(rotation.last_departure.id, { parking_position: newParking.toUpperCase() });
      }
      
      toast.success(`Parking position updated to ${newParking.toUpperCase()}`);
      setEditingCardParking(null);
      setCardParkingValue("");
      
      // Refresh main data to move aircraft on map
      await fetchData();
    } catch (error) {
      console.error("Error updating parking:", error);
      toast.error("Failed to update parking position");
    } finally {
      setSavingCardParking(false);
    }
  };

  // Handle mouse events for panning
  const handleMouseDown = (e) => {
    if (e.target === imageRef.current || e.target.closest('.apron-container')) {
      if (!draggedAircraft) {
        setIsDragging(true);
        setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      }
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && !draggedAircraft) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
    
    // Update tooltip position
    if (hoveredAircraft) {
      setTooltipPos({ x: e.clientX, y: e.clientY });
    }
    
    // Handle aircraft dragging
    if (draggedAircraft) {
      // Update aircraft position visually (will be saved on drop)
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Handle wheel zoom - extended range for new map
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.02 : 0.02;
    setZoom(prev => Math.min(Math.max(prev + delta, 0.05), 2));
  };

  // Get aircraft position on chart - uses database config if available
  const getAircraftPosition = (aircraft) => {
    const parking = aircraft.parking_position?.toUpperCase();
    if (parking && standPositions[parking]) {
      const pos = standPositions[parking];
      // If no shape is configured, use the default for this stand
      if (!pos.shape || pos.shape === "DEFAULT") {
        return { ...pos, shape: getDefaultShapeForStand(parking) };
      }
      return pos;
    }
    // Default position for unassigned aircraft (left side of chart)
    // Use registration hash for unique offset to avoid overlap
    const offset = aircraft.registration ? aircraft.registration.charCodeAt(0) % 10 * 35 : 0;
    return { x: 50, y: 550 + offset, rotation: 0, scale: 1, apron: "Unassigned", shape: "P2" };
  };

  // Handle aircraft click
  const handleAircraftClick = (aircraft, e) => {
    e.stopPropagation();
    setSelectedAircraft(aircraft);
  };

  // Handle aircraft drag start
  const handleAircraftDragStart = (aircraft, e) => {
    e.stopPropagation();
    setDraggedAircraft(aircraft);
    const rect = e.target.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  // Handle drop on stand
  const handleStandDrop = async (standId) => {
    if (!draggedAircraft) return;
    
    // Manual aircraft — check compatibility first, then update stand
    if (draggedAircraft.is_manual) {
      const aircraftType = draggedAircraft.aircraft_type;
      if (aircraftType && aircraftType !== "Unknown") {
        try {
          const result = await checkStandCompatibility(standId, aircraftType);
          if (!result.data.compatible) {
            setWarningData({
              aircraft: draggedAircraft,
              stand: standId,
              warnings: result.data.warnings,
              dimensions: result.data.dimensions,
              isManual: true
            });
            setShowWarningDialog(true);
            setDraggedAircraft(null);
            return;
          } else if (result.data.warnings?.length > 0) {
            setWarningData({
              aircraft: draggedAircraft,
              stand: standId,
              warnings: result.data.warnings,
              dimensions: result.data.dimensions,
              allowOverride: true,
              isManual: true
            });
            setShowWarningDialog(true);
            setDraggedAircraft(null);
            return;
          }
        } catch (error) {
          console.error("Compatibility check failed:", error);
        }
      }
      try {
        const response = await updateManualAircraftStand(draggedAircraft.manual_id, standId);
        if (response.data?.parking_warning) {
          toast.warning(response.data.parking_warning, { duration: 8000 });
        }
        toast.success(`${draggedAircraft.registration} moved to ${standId}`);
        fetchData();
      } catch (error) {
        console.error("Error moving manual aircraft:", error);
        toast.error("Failed to move aircraft");
      }
      setDraggedAircraft(null);
      return;
    }
    
    // Check compatibility
    try {
      const result = await checkStandCompatibility(standId, draggedAircraft.aircraft_type);
      
      if (!result.data.compatible) {
        setWarningData({
          aircraft: draggedAircraft,
          stand: standId,
          warnings: result.data.warnings,
          dimensions: result.data.dimensions
        });
        setShowWarningDialog(true);
      } else if (result.data.warnings?.length > 0) {
        // Show warning but allow
        setWarningData({
          aircraft: draggedAircraft,
          stand: standId,
          warnings: result.data.warnings,
          dimensions: result.data.dimensions,
          allowOverride: true
        });
        setShowWarningDialog(true);
      } else {
        // No issues, update directly
        await updateParking(draggedAircraft.arrival_flight_id, standId);
      }
    } catch (error) {
      console.error("Error checking compatibility:", error);
      toast.error("Failed to check stand compatibility");
    }
    
    setDraggedAircraft(null);
  };

  // Update parking position
  const updateParking = async (flightId, standId) => {
    setUpdateLoading(true);
    try {
      await updateAircraftParking(flightId, standId);
      toast.success(`Parking updated to ${standId}`);
      fetchData(); // Refresh
    } catch (error) {
      console.error("Error updating parking:", error);
      toast.error("Failed to update parking position");
    } finally {
      setUpdateLoading(false);
      setShowWarningDialog(false);
      setWarningData(null);
    }
  };

  // Confirm parking despite warnings
  const confirmParkingWithWarnings = async () => {
    if (warningData) {
      if (warningData.isManual) {
        // Manual aircraft - update stand
        try {
          const response = await updateManualAircraftStand(warningData.aircraft.manual_id, warningData.stand);
          if (response.data?.parking_warning) {
            toast.warning(response.data.parking_warning, { duration: 8000 });
          }
          toast.success(`${warningData.aircraft.registration} moved to ${warningData.stand}`);
          fetchData();
        } catch (error) {
          toast.error("Failed to move aircraft");
        }
      } else {
        updateParking(warningData.aircraft.arrival_flight_id, warningData.stand);
      }
    }
  };

  // Reset view to fit image in window
  const resetView = () => {
    setZoom(0.24);
    setPan({ x: 0, y: 0 });
  };

  // Get aircraft color based on status
  const getAircraftColor = (aircraft) => {
    return STATUS_COLORS[aircraft.color] || STATUS_COLORS.black;
  };

  // Compute stand availability in real-time
  const standAvailability = useMemo(() => {
    const result = {};
    const today = new Date().toISOString().split('T')[0];
    const currentAircraft = isHistoricalMode && historicalData?.aircraft 
      ? historicalData.aircraft 
      : aircraftOnGround;
    
    Object.entries(standPositions).forEach(([standId, pos]) => {
      const isDeactivated = pos.deactivated && 
        (!pos.deactivation_start || pos.deactivation_start <= today) &&
        (!pos.deactivation_end || pos.deactivation_end >= today);
      
      if (isDeactivated) {
        result[standId] = { state: 'closed', aircraft: null };
        return;
      }
      
      const occupant = currentAircraft.find(
        a => a.parking_position?.toUpperCase() === standId
      );
      
      if (occupant) {
        result[standId] = { state: 'occupied', aircraft: occupant };
      } else {
        result[standId] = { state: 'available', aircraft: null };
      }
    });
    
    return result;
  }, [standPositions, aircraftOnGround, isHistoricalMode, historicalData]);

  // Count stand statuses
  const availabilityCounts = useMemo(() => {
    const counts = { available: 0, occupied: 0, closed: 0 };
    Object.values(standAvailability).forEach(s => {
      counts[s.state] = (counts[s.state] || 0) + 1;
    });
    return counts;
  }, [standAvailability]);

  // Calculate scaled position for aircraft/stands
  // Aircraft positions are stored relative to the ORIGINAL map coordinates
  // Only the base zoom affects their screen position, NOT the map transform
  // The map transform only shifts/scales the background image
  const getScaledPosition = (pos) => {
    return {
      x: pos.x * zoom + pan.x,
      y: pos.y * zoom + pan.y
    };
  };

  return (
    <div className="space-y-6" data-testid="apron-management-page">
      {/* Sticky Header */}
      <div className="sticky-page-header flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0f172a] dark:text-white flex items-center gap-2">
            Apron Management
            {isHistoricalMode && (
              <Badge variant="outline" className="text-sm font-normal border-amber-500 text-amber-600">
                <History size={14} className="mr-1" />
                Historical View
              </Badge>
            )}
          </h1>
          <p className="text-[#64748b] dark:text-gray-400">
            {isHistoricalMode 
              ? `Viewing parking history for ${format(selectedDate, "PPP")}` 
              : "Visual parking stand allocation and aircraft tracking"
            }
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Display Config - Admin only */}
          {(isMasterAdmin || isAdmin) && (
            <Button 
              variant="outline" 
              className="gap-2 border-blue-500 text-blue-500 hover:bg-blue-500/10"
              onClick={() => setShowDisplayConfig(true)}
            >
              <Sliders size={16} />
              Adjust Display
            </Button>
          )}
          
          {/* Configure Positions - Master Admin only */}
          {isMasterAdmin && (
            <Link to="/parking-config">
              <Button variant="outline" className="gap-2 border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37]/10">
                <Settings size={16} />
                Configure Positions
              </Button>
            </Link>
          )}
          
          <Button onClick={fetchData} variant="outline" className="gap-2" disabled={loading}>
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Display Config Dialog - Admin only */}
      <Dialog open={showDisplayConfig} onOpenChange={setShowDisplayConfig}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sliders size={20} />
              Adjust Display Settings
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Aircraft Scale */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">
                Aircraft Size: {displayConfig.aircraftScale.toFixed(1)}x
              </Label>
              <Slider
                value={[displayConfig.aircraftScale * 10]}
                onValueChange={(v) => setDisplayConfig(prev => ({ ...prev, aircraftScale: v[0] / 10 }))}
                min={1}
                max={30}
                step={1}
                className="w-full"
              />
              <p className="text-xs text-gray-500">Adjust the size of aircraft icons on the map</p>
            </div>
            
            {/* Closed Sign Scale */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">
                Closed Sign Size: {displayConfig.signScale.toFixed(1)}x
              </Label>
              <Slider
                value={[displayConfig.signScale * 10]}
                onValueChange={(v) => setDisplayConfig(prev => ({ ...prev, signScale: v[0] / 10 }))}
                min={1}
                max={30}
                step={1}
                className="w-full"
              />
              <p className="text-xs text-gray-500">Adjust the size of closed stand indicators</p>
            </div>
            
            {/* Default Zoom */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">
                Default Map Zoom: {Math.round(displayConfig.defaultZoom * 100)}%
              </Label>
              <Slider
                value={[displayConfig.defaultZoom * 100]}
                onValueChange={(v) => setDisplayConfig(prev => ({ ...prev, defaultZoom: v[0] / 100 }))}
                min={5}
                max={100}
                step={1}
                className="w-full"
              />
              <p className="text-xs text-gray-500">Set the default zoom level when opening the page</p>
            </div>
            
            {/* Show All Positions Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Show All Stand Positions</Label>
                <p className="text-xs text-gray-500">Preview aircraft at all configured stands</p>
              </div>
              <Button
                variant={displayConfig.showAllPositions ? "default" : "outline"}
                size="sm"
                onClick={() => setDisplayConfig(prev => ({ ...prev, showAllPositions: !prev.showAllPositions }))}
              >
                {displayConfig.showAllPositions ? "On" : "Off"}
              </Button>
            </div>
            
            {/* Apply to current view */}
            <div className="flex items-center gap-2 pt-2 border-t">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setZoom(displayConfig.defaultZoom)}
              >
                Apply Zoom Now
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setDisplayConfig({
                    aircraftScale: 1.0,
                    signScale: 1.0,
                    defaultZoom: 0.24,
                    showAllPositions: false
                  });
                }}
              >
                Reset to Default
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisplayConfig(false)}>
              Cancel
            </Button>
            <Button onClick={saveDisplayConfig} disabled={savingDisplayConfig}>
              {savingDisplayConfig ? "Saving..." : "Save Settings"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Legend */}
      <Card className="bg-white dark:bg-[#1e293b]">
        <CardContent className="py-3">
          <div className="flex items-center gap-6 flex-wrap justify-between">
            <div className="flex items-center gap-6 flex-wrap">
              <span className="text-sm font-medium text-[#64748b] dark:text-gray-400">Departure Status:</span>
              {Object.entries(STATUS_COLORS).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2">
                  <div 
                    className="w-4 h-4 rounded"
                    style={{ backgroundColor: value.bg }}
                  />
                  <span className="text-sm text-[#0f172a] dark:text-white">{value.label}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-4" data-testid="stand-availability-summary">
              <span className="text-sm font-medium text-[#64748b] dark:text-gray-400">Stands:</span>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-blue-400 ring-2 ring-blue-200" />
                <span className="text-sm font-semibold text-blue-500">{availabilityCounts.available}</span>
                <span className="text-xs text-[#64748b]">Available</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500 ring-2 ring-red-200" />
                <span className="text-sm font-semibold text-red-600">{availabilityCounts.occupied}</span>
                <span className="text-xs text-[#64748b]">Occupied</span>
              </div>
              {availabilityCounts.closed > 0 && (
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-gray-500 ring-2 ring-gray-200" />
                  <span className="text-sm font-semibold text-gray-600">{availabilityCounts.closed}</span>
                  <span className="text-xs text-[#64748b]">Closed</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Active Stand Closures Alert - Visible to ALL users */}
      {standClosures.filter(c => c.status === "active" || c.status === "upcoming").length > 0 && (
        <Card className="bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-800 border-2" data-testid="closure-alert-banner">
          <CardContent className="py-3">
            <div className="flex items-start gap-3">
              <Ban size={24} className="text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-bold text-red-700 dark:text-red-400 mb-2 flex items-center gap-2">
                  <span>Stand Closures</span>
                  <Badge className="bg-red-600 text-white">
                    {standClosures.filter(c => c.status === "active").length} Active
                  </Badge>
                  {standClosures.filter(c => c.status === "upcoming").length > 0 && (
                    <Badge className="bg-amber-500 text-white">
                      {standClosures.filter(c => c.status === "upcoming").length} Upcoming
                    </Badge>
                  )}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {standClosures
                    .filter(c => c.status === "active" || c.status === "upcoming")
                    .map((closure) => (
                      <div 
                        key={closure.id}
                        className={`p-2 rounded-lg text-sm ${
                          closure.status === "active" 
                            ? "bg-red-100 dark:bg-red-900/40 border border-red-300 dark:border-red-700"
                            : "bg-amber-100 dark:bg-amber-900/40 border border-amber-300 dark:border-amber-700"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Badge className={closure.status === "active" ? "bg-red-600" : "bg-amber-500"}>
                            {closure.stand_id}
                          </Badge>
                          <span className={closure.status === "active" ? "text-red-700 dark:text-red-300 font-medium" : "text-amber-700 dark:text-amber-300"}>
                            {closure.status === "active" ? "CLOSED" : "Scheduled"}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          {format(parseISO(closure.start_date), "MMM d")} - {format(parseISO(closure.end_date), "MMM d, yyyy")}
                        </div>
                        {closure.reason && (
                          <div className="text-xs italic text-gray-500 dark:text-gray-400 mt-0.5">
                            {closure.reason}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Historical Parking Summary */}
      {isHistoricalMode && historicalData && (
        <Card className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-amber-700 dark:text-amber-400">
              <History size={20} />
              Historical Parking Usage - {format(selectedDate, "PPP")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="text-center p-3 bg-white dark:bg-[#1e293b] rounded-lg">
                <div className="text-2xl font-bold text-[#d4af37]">{historicalData.aircraft_count}</div>
                <div className="text-xs text-[#64748b]">Aircraft On Ground</div>
              </div>
              <div className="text-center p-3 bg-white dark:bg-[#1e293b] rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{historicalData.stands_used}</div>
                <div className="text-xs text-[#64748b]">Stands Used</div>
              </div>
            </div>
            
            {/* Stand Usage Breakdown */}
            {Object.keys(historicalData.parking_usage || {}).length > 0 && (
              <div>
                <p className="text-sm font-medium text-amber-700 dark:text-amber-400 mb-2">Stand Usage Details:</p>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {Object.entries(historicalData.parking_usage || {}).map(([stand, aircraft]) => (
                    <div 
                      key={stand} 
                      className="p-2 bg-white dark:bg-[#1e293b] rounded-lg border border-amber-200 dark:border-amber-800"
                    >
                      <div className="font-bold text-[#d4af37] text-sm">{stand}</div>
                      {aircraft.map((ac, idx) => (
                        <div key={idx} className="text-xs text-[#64748b]">
                          {ac.registration} ({ac.aircraft_type})
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Apron Chart */}
        <Card className="lg:col-span-3 bg-white dark:bg-[#1e293b] overflow-hidden">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin size={20} className="text-[#d4af37]" />
                Apron Chart - EBCI
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setZoom(z => Math.max(z - 0.02, 0.05))}>
                  <ZoomOut size={16} />
                </Button>
                <span className="text-sm text-[#64748b] w-12 text-center">{Math.round(zoom * 100)}%</span>
                <Button variant="ghost" size="sm" onClick={() => setZoom(z => Math.min(z + 0.02, 2))}>
                  <ZoomIn size={16} />
                </Button>
                <Button variant="ghost" size="sm" onClick={resetView} title="Reset View">
                  <Maximize2 size={16} />
                </Button>
                <Button variant="ghost" size="sm" onClick={fitToScreen} title="Fit to Screen" className="text-xs">
                  Fit
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div 
              ref={containerRef}
              className="apron-container relative overflow-hidden bg-[#f8fafc] dark:bg-[#0f172a] cursor-grab active:cursor-grabbing"
              style={{ height: "700px" }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onWheel={handleWheel}
            >
              {/* Apron Image - with map transform applied */}
              <img
                ref={imageRef}
                src={apronMapUrl}
                alt="EBCI Apron Chart"
                className="absolute pointer-events-none select-none"
                style={{
                  transform: `translate(${pan.x + (mapTransform.offsetX * zoom)}px, ${pan.y + (mapTransform.offsetY * zoom)}px) scale(${zoom * mapTransform.scale})`,
                  transformOrigin: "top left",
                  maxWidth: "none"
                }}
                draggable={false}
                onLoad={handleImageLoad}
              />

              {/* Aircraft Icons - Current or Historical */}
              {(isHistoricalMode && historicalData?.aircraft ? historicalData.aircraft : aircraftOnGround).map((aircraft) => {
                // Build aircraft data structure for historical mode
                const aircraftData = isHistoricalMode ? {
                  registration: aircraft.registration,
                  aircraft_type: aircraft.aircraft_type,
                  operator: aircraft.operator,
                  parking_position: aircraft.parking_position,
                  arrival_date: aircraft.arrival_date,
                  departure_date: aircraft.departure_date,
                  dimensions: { wingspan: 15 } // Default for historical
                } : aircraft;
                
                const pos = getAircraftPosition(aircraftData);
                const scaledPos = getScaledPosition(pos);
                const isSelected = selectedAircraft?.registration === aircraftData.registration;
                const isHovered = hoveredAircraft?.registration === aircraftData.registration;
                const isDragged = draggedAircraft?.registration === aircraftData.registration;
                
                // Historical mode uses RED color for clear distinction
                const color = isHistoricalMode 
                  ? { bg: "#dc2626", border: "#b91c1c" }
                  : getAircraftColor(aircraft);
                
                // Use position scale from config directly - this is already configured in parking config
                const posScale = pos.scale || 1;
                // Base size for aircraft icon - scaled with display config multiplier
                const baseSize = 60 * displayConfig.aircraftScale;
                const aircraftSize = Math.max(baseSize * posScale * zoom, 20); // Minimum 20px
                
                // Get aircraft shape - use configured shape, or auto-detect from stand ID
                const standId = aircraftData.parking_position?.toUpperCase();
                const shapeKey = pos.shape || getDefaultShapeForStand(standId) || "DEFAULT";
                const shape = AIRCRAFT_SHAPES[shapeKey] || AIRCRAFT_SHAPES["DEFAULT"];
                
                return (
                  <div
                    key={aircraftData.registration}
                    className={`absolute cursor-pointer transition-all duration-200 ${
                      isSelected ? `z-30 ring-2 ${isHistoricalMode ? "ring-red-600" : "ring-[#d4af37]"} ring-offset-2` : "z-20"
                    } ${isDragged ? "opacity-50" : ""}`}
                    style={{
                      left: scaledPos.x - aircraftSize / 2,
                      top: scaledPos.y - aircraftSize / 2,
                      width: `${aircraftSize}px`,
                      height: `${aircraftSize}px`,
                      transform: `rotate(${pos.rotation}deg)`,
                      transformOrigin: "center center",
                    }}
                    onClick={(e) => !isHistoricalMode && handleAircraftClick(aircraftData, e)}
                    onMouseEnter={(e) => {
                      setHoveredAircraft(aircraftData);
                      setTooltipPos({ x: e.clientX, y: e.clientY });
                    }}
                    onMouseLeave={() => setHoveredAircraft(null)}
                    onMouseDown={(e) => !isHistoricalMode && handleAircraftDragStart(aircraftData, e)}
                    draggable={false}
                    data-testid={`aircraft-${aircraftData.registration}`}
                  >
                    <svg width="100%" height="100%" viewBox={shape.viewBox} className="drop-shadow-lg">
                      {/* Aircraft silhouette using configured shape */}
                      <path
                        d={shape.path}
                        fill={color.bg}
                        stroke={isSelected || isHovered ? "#d4af37" : "#fff"}
                        strokeWidth={isSelected || isHovered ? "2" : "1"}
                      />
                    </svg>
                    {/* Registration label - counter-rotated so it stays readable */}
                    <div
                      className="absolute left-1/2 pointer-events-none whitespace-nowrap"
                      style={{
                        top: `${aircraftSize + 2}px`,
                        transform: `translateX(-50%) rotate(${-pos.rotation}deg)`,
                        fontSize: `${Math.max(9, Math.min(12, aircraftSize / 4))}px`,
                        lineHeight: 1,
                      }}
                    >
                      <span
                        className="px-1 py-0.5 rounded font-bold text-white shadow-sm"
                        style={{ backgroundColor: color.bg, opacity: 0.95 }}
                      >
                        {aircraftData.registration}
                      </span>
                    </div>
                  </div>
                );
              })}

              {/* Stand Hotspots with Availability Overlay */}
              {Object.entries(standPositions).map(([standId, pos]) => {
                const scaledPos = getScaledPosition(pos);
                const hasAircraft = aircraftOnGround.some(
                  a => a.parking_position?.toUpperCase() === standId
                );
                
                // Check if stand is currently deactivated
                const today = new Date().toISOString().split('T')[0];
                const isDeactivated = pos.deactivated && 
                  (!pos.deactivation_start || pos.deactivation_start <= today) &&
                  (!pos.deactivation_end || pos.deactivation_end >= today);
                
                // Use the stand's configured scale for the deactivation sign - with display config multiplier
                const posScale = pos.scale || 1;
                const signSize = Math.max(40 * displayConfig.signScale * posScale * zoom, 20); // Minimum 20px
                
                // Stand availability status
                const status = standAvailability[standId];
                const isAvailable = status?.state === 'available';
                
                // Label font size scales with zoom
                const labelSize = Math.max(8, Math.min(13, signSize / 3));
                
                // Check if this stand should show visual indicators
                const showVisual = shouldShowVisualIndicator(standId, pos);
                
                return (
                  <div key={standId}>
                    {/* Stand status indicator ring - only for visual-indicator stands */}
                    {!isDeactivated && showVisual && (
                      <div
                        className={`absolute rounded-full pointer-events-none transition-all duration-300 ${
                          isAvailable 
                            ? "border-2 border-blue-400/60 bg-blue-400/8" 
                            : ""
                        }`}
                        style={{
                          left: scaledPos.x - signSize / 2 - 4,
                          top: scaledPos.y - signSize / 2 - 4,
                          width: `${signSize + 8}px`,
                          height: `${signSize + 8}px`,
                          zIndex: isAvailable ? 5 : 1,
                        }}
                        data-testid={`stand-indicator-${standId}`}
                      >
                        {/* Subtle glow for available stands */}
                        {isAvailable && (
                          <div className="absolute inset-0 rounded-full border border-blue-300/30" />
                        )}
                      </div>
                    )}
                    
                    {/* Stand label - only for visual-indicator stands */}
                    {showVisual && (
                      <div
                        className="absolute pointer-events-none z-10"
                        style={{
                          left: scaledPos.x,
                          top: scaledPos.y - signSize / 2 - labelSize - 4,
                          transform: 'translateX(-50%)',
                        }}
                      >
                        <span
                          className={`px-1 rounded font-bold shadow-sm inline-block ${
                            isDeactivated 
                              ? "bg-gray-600 text-white" 
                              : isAvailable 
                                ? "bg-blue-500/70 text-white" 
                                : "bg-slate-700/80 text-white"
                          }`}
                          style={{ fontSize: `${labelSize}px`, lineHeight: 1.3 }}
                        >
                          {standId}
                        </span>
                      </div>
                    )}
                    
                    {/* Drop target / Deactivation sign */}
                    <div
                      className={`absolute transition-all ${
                        isDeactivated
                          ? "z-30"
                          : draggedAircraft && !hasAircraft
                            ? "border-[#d4af37] bg-[#d4af37]/20 z-40 rounded-full border-2"
                            : "border-transparent rounded-full border-2"
                      }`}
                      style={{
                        left: scaledPos.x - signSize / 2,
                        top: scaledPos.y - signSize / 2,
                        width: `${signSize}px`,
                        height: `${signSize}px`,
                      }}
                      onMouseUp={() => !isDeactivated && handleStandDrop(standId)}
                      title={isDeactivated ? `${standId} - Deactivated: ${pos.deactivation_reason || "Closed"}` : `${standId} - ${isAvailable ? 'Available' : 'Occupied'}`}
                    >
                      {/* No Entry Sign for deactivated stands */}
                      {isDeactivated && (
                        <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-lg">
                          {/* Red circle */}
                          <circle cx="50" cy="50" r="46" fill="#dc2626" stroke="#991b1b" strokeWidth="4"/>
                          {/* White horizontal bar */}
                          <rect x="15" y="40" width="70" height="20" fill="white" rx="2"/>
                          {/* Shine effect */}
                          <ellipse cx="50" cy="30" rx="30" ry="15" fill="rgba(255,255,255,0.2)"/>
                        </svg>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Preview Aircraft at All Positions - When Show All Positions is enabled */}
              {displayConfig.showAllPositions && Object.entries(standPositions).map(([standId, pos]) => {
                const scaledPos = getScaledPosition(pos);
                const hasRealAircraft = aircraftOnGround.some(
                  a => a.parking_position?.toUpperCase() === standId
                );
                
                // Skip if there's already a real aircraft here
                if (hasRealAircraft) return null;
                
                // Check if stand is deactivated
                const today = new Date().toISOString().split('T')[0];
                const isDeactivated = pos.deactivated && 
                  (!pos.deactivation_start || pos.deactivation_start <= today) &&
                  (!pos.deactivation_end || pos.deactivation_end >= today);
                
                if (isDeactivated) return null;
                
                const posScale = pos.scale || 1;
                const baseSize = 60 * displayConfig.aircraftScale;
                const aircraftSize = Math.max(baseSize * posScale * zoom, 20);
                
                const shapeKey = pos.shape || getDefaultShapeForStand(standId) || "DEFAULT";
                const shape = AIRCRAFT_SHAPES[shapeKey] || AIRCRAFT_SHAPES["DEFAULT"];
                
                return (
                  <div
                    key={`preview-${standId}`}
                    className="absolute opacity-30 pointer-events-none z-10"
                    style={{
                      left: scaledPos.x - aircraftSize / 2,
                      top: scaledPos.y - aircraftSize / 2,
                      width: `${aircraftSize}px`,
                      height: `${aircraftSize}px`,
                      transform: `rotate(${pos.rotation}deg)`,
                      transformOrigin: "center center",
                    }}
                    title={`Preview: Stand ${standId}`}
                  >
                    <svg width="100%" height="100%" viewBox={shape.viewBox}>
                      <path
                        d={shape.path}
                        fill="#94a3b8"
                        stroke="#64748b"
                        strokeWidth="1"
                      />
                    </svg>
                    {/* Stand label */}
                    <div 
                      className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-xs font-bold text-gray-500 bg-white/80 px-1 rounded"
                      style={{ fontSize: `${Math.max(10, aircraftSize / 5)}px` }}
                    >
                      {standId}
                    </div>
                  </div>
                );
              })}

              {/* Tooltip */}
              {hoveredAircraft && !isDragging && (
                <div
                  className="fixed z-50 bg-[#0f172a] text-white p-3 rounded-lg shadow-xl text-sm max-w-xs pointer-events-none"
                  style={{
                    left: tooltipPos.x + 15,
                    top: tooltipPos.y + 15
                  }}
                >
                  {isHistoricalMode && (
                    <div className="bg-red-600 text-white text-xs px-2 py-0.5 rounded mb-2 inline-block">
                      Historical Record
                    </div>
                  )}
                  <div className={`font-bold mb-1 ${isHistoricalMode ? "text-red-500" : "text-[#d4af37]"}`}>{hoveredAircraft.registration}</div>
                  <div className="space-y-1 text-xs">
                    <div><span className="text-gray-400">Type:</span> {hoveredAircraft.aircraft_type}</div>
                    <div><span className="text-gray-400">Operator:</span> {hoveredAircraft.operator || "Unknown"}</div>
                    <div><span className="text-gray-400">Parking:</span> {hoveredAircraft.parking_position || "Unassigned"}</div>
                    {isHistoricalMode ? (
                      <>
                        <div className="pt-1 border-t border-gray-700">
                          <span className="text-gray-400">Arrived:</span> {hoveredAircraft.arrival_date}
                        </div>
                        {hoveredAircraft.departure_date && (
                          <div>
                            <span className="text-gray-400">Departed:</span> {hoveredAircraft.departure_date}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="pt-1 border-t border-gray-700">
                          <span className="text-gray-400">Arrived:</span> {hoveredAircraft.arrival_date} {hoveredAircraft.arrival_time} from {hoveredAircraft.arrival_from}
                        </div>
                        {hoveredAircraft.departure ? (
                          <div>
                            <span className="text-gray-400">Departing:</span> {hoveredAircraft.departure.flight_date} {hoveredAircraft.departure.time} to {hoveredAircraft.departure.destination}
                          </div>
                        ) : (
                          <div className="text-yellow-400 font-semibold">Long Term Parking - No departure scheduled</div>
                        )}
                        <div className="pt-1 border-t border-gray-700 text-gray-400">
                          Wingspan: {hoveredAircraft.dimensions?.wingspan?.toFixed(1)}m | 
                          Length: {hoveredAircraft.dimensions?.length?.toFixed(1)}m
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Loading overlay */}
              {loading && (
                <div className="absolute inset-0 bg-white/50 dark:bg-black/50 flex items-center justify-center z-50">
                  <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Aircraft List */}
        <Card className="bg-white dark:bg-[#1e293b]">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Plane size={20} className="text-[#d4af37]" />
              {isHistoricalMode ? "Historical Aircraft" : "Aircraft on Ground"}
              <Badge variant="outline" className={`ml-auto ${isHistoricalMode ? "border-amber-500 text-amber-600" : ""}`}>
                {isHistoricalMode 
                  ? (historicalData?.aircraft?.length || 0) 
                  : aircraftOnGround.length
                }
              </Badge>
            </CardTitle>
            {/* Date Picker - Moved here near Aircraft on Ground */}
            <div className="flex items-center gap-2 pt-2">
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className={`gap-2 flex-1 ${isHistoricalMode ? "border-amber-500 text-amber-600" : ""}`}>
                    <CalendarIcon size={14} />
                    {format(selectedDate, "PPP")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white dark:bg-[#1e293b]" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    defaultMonth={selectedDate}
                    onSelect={(date) => {
                      if (date) {
                        setSelectedDate(date);
                        setCalendarOpen(false);
                      }
                    }}
                    modifiers={{
                      hasFlights: flightDates
                    }}
                    modifiersClassNames={{
                      hasFlights: "day-has-flights"
                    }}
                    initialFocus
                  />
                  <div className="px-3 py-2 border-t dark:border-[#334155] text-xs text-[#64748b] dark:text-gray-300 flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#d4af37]"></div>
                    <span>Days with flights</span>
                  </div>
                </PopoverContent>
              </Popover>
              {/* Today button when viewing a PAST historical date */}
              {isHistoricalMode && format(selectedDate, "yyyy-MM-dd") !== format(new Date(), "yyyy-MM-dd") && (
                <Button 
                  onClick={() => { setSelectedDate(new Date()); setShowTodayHistory(false); }} 
                  variant="default" 
                  size="sm"
                  className="gap-1 bg-[#d4af37] hover:bg-[#b8962d]"
                >
                  Today
                </Button>
              )}
              {/* Toggle today's history (incl. departed) vs live on-ground view */}
              {format(selectedDate, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd") && (
                <Button
                  onClick={() => setShowTodayHistory((v) => !v)}
                  variant={showTodayHistory ? "default" : "outline"}
                  size="sm"
                  className={showTodayHistory ? "gap-1 bg-[#d4af37] hover:bg-[#b8962d]" : "gap-1 border-amber-500 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20"}
                  title={showTodayHistory ? "Back to live on-ground view" : "Show today's history including departed aircraft"}
                  data-testid="today-history-toggle"
                >
                  <History size={14} />
                  {showTodayHistory ? "Live View" : "Show Departed"}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {/* Manual Aircraft Add Button */}
            <div className="px-4 pt-3 pb-1">
              <Button
                variant={showManualForm ? "secondary" : "outline"}
                size="sm"
                className="w-full gap-2 border-violet-500 text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20"
                onClick={() => {
                  const opening = !showManualForm;
                  setShowManualForm(opening);
                  if (opening && isHistoricalMode && !manualArrival) {
                    const d = new Date(selectedDate);
                    const s = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T05:00`;
                    setManualArrival(s);
                  }
                }}
                data-testid="add-manual-aircraft-btn"
              >
                <Plus size={14} />
                {showManualForm ? "Cancel" : "Add External Aircraft"}
              </Button>
            </div>
            
            {/* Manual Aircraft Form */}
            {showManualForm && (
              <div className="mx-4 mt-2 p-3 rounded-lg border-2 border-violet-300 dark:border-violet-700 bg-violet-50/50 dark:bg-violet-900/10 space-y-2" data-testid="manual-aircraft-form">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-violet-500" />
                  <span className="text-xs font-semibold text-violet-700 dark:text-violet-300">External Aircraft{isHistoricalMode ? ` — ${format(selectedDate, "PP")}` : ""}</span>
                </div>
                {/* Registration + Lookup */}
                <div className="relative">
                  <Input
                    placeholder="Registration (e.g. DISIR)"
                    value={manualReg}
                    onChange={(e) => handleManualRegLookup(e.target.value)}
                    className="h-8 text-xs uppercase pr-8"
                    data-testid="manual-reg-input"
                  />
                  {manualLookupLoading && <Loader2 size={12} className="absolute right-2 top-2 animate-spin text-violet-500" />}
                  {!manualLookupLoading && manualReg.length >= 3 && <Search size={12} className="absolute right-2 top-2 text-gray-400" />}
                </div>
                {/* Auto-filled info */}
                {(manualOperator || manualAircraftType) && (
                  <div className="text-[10px] text-violet-600 dark:text-violet-400 bg-violet-100 dark:bg-violet-900/30 px-2 py-1 rounded">
                    {manualAircraftType && <span className="font-medium">{manualAircraftType}</span>}
                    {manualAircraftType && manualOperator && <span> — </span>}
                    {manualOperator && <span>{manualOperator}</span>}
                  </div>
                )}
                {/* Stand Selector - Dynamic from parking config apron groups */}
                <Popover open={manualStandOpen} onOpenChange={setManualStandOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-full h-8 text-xs justify-between" data-testid="manual-stand-select">
                      {manualStand || "Select Stand"}
                      <MapPin size={12} />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-3 max-h-64 overflow-y-auto" align="start">
                    <div className="space-y-2">
                      {(() => {
                        const groups = {};
                        Object.entries(standPositions).forEach(([id, pos]) => {
                          const apron = pos.apron || "Other";
                          if (!groups[apron]) groups[apron] = [];
                          groups[apron].push(id);
                        });
                        Object.keys(groups).forEach(k => {
                          groups[k].sort((a, b) => {
                            const numA = parseInt(a.replace(/[^0-9]/g, '')) || 0;
                            const numB = parseInt(b.replace(/[^0-9]/g, '')) || 0;
                            return numA !== numB ? numA - numB : a.localeCompare(b);
                          });
                        });
                        const order = ["P1", "P2", "P3", "P4", "P5", "Hangars", "Other"];
                        const sortedKeys = Object.keys(groups).sort((a, b) => {
                          const ia = order.indexOf(a); const ib = order.indexOf(b);
                          return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
                        });
                        return sortedKeys.map(apron => (
                          <div key={apron}>
                            <p className="text-[10px] text-[#d4af37] font-medium">{apron}</p>
                            <div className="grid grid-cols-6 gap-1">
                              {groups[apron].map(s => {
                                const status = standAvailability[s];
                                const isOccupied = status?.state === 'occupied';
                                const isClosed = status?.state === 'closed';
                                const isUnavailable = isOccupied || isClosed;
                                const occupantReg = status?.aircraft?.registration;
                                return (
                                  <Button key={s} variant={manualStand===s?"default":"outline"} size="sm"
                                    className={`h-6 text-[10px] p-0 relative ${
                                      manualStand===s ? "bg-violet-500 hover:bg-violet-600" 
                                      : isUnavailable ? "opacity-40 cursor-not-allowed line-through bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700 text-red-400" 
                                      : ""
                                    }`}
                                    disabled={isUnavailable}
                                    title={isOccupied ? `Occupied by ${occupantReg}` : isClosed ? "Stand closed" : s}
                                    onClick={() => { if (!isUnavailable) { setManualStand(s); setManualStandOpen(false); } }}
                                  >{s}</Button>
                                );
                              })}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  </PopoverContent>
                </Popover>
                {/* 24h Checkbox */}
                <div className="flex items-center gap-2 pt-1">
                  <input
                    type="checkbox"
                    id="manual-24h-check"
                    checked={manual24h}
                    onChange={(e) => handleManual24hToggle(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-violet-600 focus:ring-violet-500 accent-violet-600"
                    data-testid="manual-24h-checkbox"
                  />
                  <Label htmlFor="manual-24h-check" className="text-[10px] text-gray-600 dark:text-gray-400 cursor-pointer select-none">
                    24h period (05:00 → next day 05:00)
                  </Label>
                </div>
                {/* Arrival Time */}
                <div>
                  <Label className="text-[10px] text-gray-500">Arrival{manual24h ? "" : " (optional)"}</Label>
                  <Input
                    type="datetime-local"
                    value={manualArrival}
                    onChange={(e) => { if (!manual24h) setManualArrival(e.target.value); }}
                    className={`h-8 text-xs ${manual24h ? "opacity-60 cursor-not-allowed bg-gray-50 dark:bg-gray-800" : ""}`}
                    readOnly={manual24h}
                    data-testid="manual-arrival-input"
                  />
                </div>
                {/* Departure Time */}
                <div>
                  <Label className="text-[10px] text-gray-500">Departure{manual24h ? "" : " (optional)"}</Label>
                  <Input
                    type="datetime-local"
                    value={manualDeparture}
                    onChange={(e) => { if (!manual24h) setManualDeparture(e.target.value); }}
                    className={`h-8 text-xs ${manual24h ? "opacity-60 cursor-not-allowed bg-gray-50 dark:bg-gray-800" : ""}`}
                    readOnly={manual24h}
                    data-testid="manual-departure-input"
                  />
                </div>
                {/* Submit */}
                <Button
                  size="sm"
                  className="w-full h-8 bg-violet-600 hover:bg-violet-700 text-white gap-2"
                  onClick={handleManualSubmit}
                  disabled={savingManual || !manualReg || !manualStand}
                  data-testid="manual-submit-btn"
                >
                  {savingManual ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  Add to Apron
                </Button>
              </div>
            )}

            <ScrollArea className="h-[500px]">
              <div className="p-4 space-y-2">
                {/* Loading state */}
                {loading && (
                  <div className="text-center py-8 text-[#64748b]">
                    <Loader2 size={40} className="mx-auto mb-2 animate-spin text-[#d4af37]" />
                    <p>Loading parking data...</p>
                  </div>
                )}
                {/* Historical Mode */}
                {!loading && isHistoricalMode ? (
                  !historicalData || historicalData?.aircraft?.length === 0 ? (
                    <div className="text-center py-8 text-[#64748b]">
                      <Plane size={40} className="mx-auto mb-2 opacity-30" />
                      <p>No aircraft parked on this date</p>
                    </div>
                  ) : (
                    // Sort by arrival date/time chronologically
                    [...(historicalData?.aircraft || [])]
                      .sort((a, b) => {
                        const dateTimeA = `${a.arrival_date || ''} ${a.arrival_time || '00:00'}`;
                        const dateTimeB = `${b.arrival_date || ''} ${b.arrival_time || '00:00'}`;
                        return dateTimeA.localeCompare(dateTimeB);
                      })
                      .map((aircraft) => (
                      <div
                        key={aircraft.registration}
                        className={`p-3 rounded-lg border ${aircraft.is_manual
                          ? "border-violet-300 bg-violet-50/60 dark:bg-violet-900/20 dark:border-violet-800"
                          : "border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800"}`}
                        data-testid={`historical-aircraft-${aircraft.registration}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`w-3 h-3 rounded-full ${aircraft.is_manual ? "bg-violet-500" : "bg-red-600"}`} />
                          <span className="font-bold text-[#0f172a] dark:text-white">
                            {aircraft.registration}
                          </span>
                          {aircraft.is_manual ? (
                            <>
                              <Badge className="bg-violet-500 text-white text-[9px] px-1.5 py-0">Manual</Badge>
                              <div className="ml-auto flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-1.5 text-violet-600 hover:text-violet-800 hover:bg-violet-100"
                                  onClick={() => openEditManual(aircraft)}
                                  data-testid={`edit-manual-${aircraft.registration}`}
                                >
                                  <Edit2 size={12} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-1.5 text-red-500 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleManualDelete(aircraft.manual_id, aircraft.registration)}
                                  disabled={deletingManualId === aircraft.manual_id}
                                  data-testid={`delete-manual-hist-${aircraft.registration}`}
                                >
                                  {deletingManualId === aircraft.manual_id
                                    ? <Loader2 size={12} className="animate-spin" />
                                    : <Trash2 size={12} />}
                                </Button>
                              </div>
                            </>
                          ) : (
                            <Badge variant="outline" className="ml-auto text-xs border-red-500 text-red-600">
                              Historical
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-[#64748b] space-y-1">
                          <div className="flex justify-between">
                            <span>Type:</span>
                            <span className="font-medium text-[#0f172a] dark:text-white">{aircraft.aircraft_type}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Stand:</span>
                            <span className={`font-medium ${aircraft.is_manual ? "text-violet-600" : "text-red-600"}`}>{aircraft.parking_position || "-"}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Arrived:</span>
                            <span>{aircraft.arrival_date} {aircraft.arrival_time || ""}</span>
                          </div>
                          {aircraft.departure_date && (
                            <div className="flex justify-between">
                              <span>Departed:</span>
                              <span>{aircraft.departure_date} {aircraft.departure_time || ""}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )
                ) : !loading ? (
                  /* Current Mode */
                  aircraftOnGround.length === 0 ? (
                    <div className="text-center py-8 text-[#64748b]">
                      <Plane size={40} className="mx-auto mb-2 opacity-30" />
                      <p>No aircraft on ground</p>
                    </div>
                  ) : (
                    aircraftOnGround.map((aircraft) => {
                      const color = getAircraftColor(aircraft);
                      const isSelected = selectedAircraft?.registration === aircraft.registration;
                      const isEditingParking = editingCardParking === aircraft.registration;
                      const isManual = aircraft.is_manual;
                      
                      return (
                        <div
                          key={aircraft.registration}
                          className={`p-3 rounded-lg border transition-all ${
                            isManual
                              ? "border-violet-300 dark:border-violet-700 bg-violet-50/50 dark:bg-violet-900/10"
                              : isSelected
                                ? "border-[#d4af37] bg-[#d4af37]/10"
                                : "border-[#e2e8f0] dark:border-[#334155] hover:border-[#d4af37]/50"
                          }`}
                          data-testid={`aircraft-list-${aircraft.registration}`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <div 
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: color.bg }}
                            />
                            <span className="font-bold text-[#0f172a] dark:text-white">
                              {aircraft.registration}
                            </span>
                            {isManual && (
                              <Badge className="bg-violet-500 text-white text-[9px] px-1.5 py-0">Manual</Badge>
                            )}
                            {!isManual && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="ml-auto h-6 px-2"
                                onClick={() => openRotationDialog(aircraft)}
                              >
                                <RotateCw size={12} className="mr-1" />
                                Rotation
                              </Button>
                            )}
                            {isManual && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="ml-auto h-6 px-2 text-violet-600 hover:text-violet-800 hover:bg-violet-100"
                                onClick={() => openEditManual(aircraft)}
                                data-testid={`edit-manual-${aircraft.registration}`}
                              >
                                <Edit2 size={12} />
                              </Button>
                            )}
                            {isManual && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleManualDelete(aircraft.manual_id, aircraft.registration)}
                                disabled={deletingManualId === aircraft.manual_id}
                                data-testid={`delete-manual-${aircraft.registration}`}
                              >
                                {deletingManualId === aircraft.manual_id 
                                  ? <Loader2 size={12} className="animate-spin" /> 
                                  : <Trash2 size={12} />}
                              </Button>
                            )}
                            {/* Editable Parking Position - not for manual entries */}
                            {!isManual && isEditingParking ? (
                              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button variant="outline" size="sm" className="h-7 w-20 text-xs justify-between">
                                      {cardParkingValue || "Stand"}
                                      <MapPin size={10} />
                                    </Button>
                                  </PopoverTrigger>
                                <PopoverContent className="w-80 p-3" align="end">
                                  <div className="space-y-3">
                                    <p className="text-xs font-semibold text-[#64748b]">Select Parking Stand</p>
                                    
                                    {/* P1 Stands */}
                                    <div>
                                      <p className="text-[10px] text-[#d4af37] font-medium mb-1">P1</p>
                                      <div className="grid grid-cols-8 gap-1">
                                        {["1A", "1B", "2A", "2B", "3A", "3B", "4A", "4B"].map(s => {
                                          const st = standAvailability[s];
                                          const blocked = st?.state === 'occupied' || st?.state === 'closed';
                                          return (
                                            <Button key={s} variant={cardParkingValue === s ? "default" : "outline"} size="sm"
                                              className={`h-7 text-[10px] p-0 ${cardParkingValue === s ? "bg-[#d4af37]" : blocked ? "opacity-40 line-through text-red-400" : ""}`}
                                              disabled={blocked}
                                              title={blocked ? `Occupied by ${st?.aircraft?.registration || 'closed'}` : s}
                                              onClick={() => setCardParkingValue(s)}>{s}</Button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                    
                                    {/* P3 Stands */}
                                    <div>
                                      <p className="text-[10px] text-[#d4af37] font-medium mb-1">P3</p>
                                      <div className="grid grid-cols-8 gap-1">
                                        {["17B", "17C", "18B", "18C"].map(s => {
                                          const st = standAvailability[s];
                                          const blocked = st?.state === 'occupied' || st?.state === 'closed';
                                          return (
                                            <Button key={s} variant={cardParkingValue === s ? "default" : "outline"} size="sm"
                                              className={`h-7 text-[10px] p-0 ${cardParkingValue === s ? "bg-[#d4af37]" : blocked ? "opacity-40 line-through text-red-400" : ""}`}
                                              disabled={blocked}
                                              title={blocked ? `Occupied by ${st?.aircraft?.registration || 'closed'}` : s}
                                              onClick={() => setCardParkingValue(s)}>{s}</Button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                    
                                    {/* P4 Stands */}
                                    <div>
                                      <p className="text-[10px] text-[#d4af37] font-medium mb-1">P4</p>
                                      <div className="grid grid-cols-8 gap-1">
                                        {["19A", "19B", "20A", "20B", "21A", "21B", "22A", "22B", 
                                          "23A", "23B", "24A", "24B", "25A", "25B", "26A", "26B",
                                          "27A", "27B", "28A", "28B"].map(s => {
                                          const st = standAvailability[s];
                                          const blocked = st?.state === 'occupied' || st?.state === 'closed';
                                          return (
                                            <Button key={s} variant={cardParkingValue === s ? "default" : "outline"} size="sm"
                                              className={`h-7 text-[10px] p-0 ${cardParkingValue === s ? "bg-[#d4af37]" : blocked ? "opacity-40 line-through text-red-400" : ""}`}
                                              disabled={blocked}
                                              title={blocked ? `Occupied by ${st?.aircraft?.registration || 'closed'}` : s}
                                              onClick={() => setCardParkingValue(s)}>{s}</Button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                    
                                    {/* P5 Stands */}
                                    <div>
                                      <p className="text-[10px] text-[#d4af37] font-medium mb-1">P5</p>
                                      <div className="grid grid-cols-8 gap-1">
                                        {["30", "31", "32", "33", "34", "35", "36", "37", "38", "39"].map(s => {
                                          const st = standAvailability[s];
                                          const blocked = st?.state === 'occupied' || st?.state === 'closed';
                                          return (
                                            <Button key={s} variant={cardParkingValue === s ? "default" : "outline"} size="sm"
                                              className={`h-7 text-[10px] p-0 ${cardParkingValue === s ? "bg-[#d4af37]" : blocked ? "opacity-40 line-through text-red-400" : ""}`}
                                              disabled={blocked}
                                              title={blocked ? `Occupied by ${st?.aircraft?.registration || 'closed'}` : s}
                                              onClick={() => setCardParkingValue(s)}>{s}</Button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  </div>
                                </PopoverContent>
                              </Popover>
                              <Button 
                                size="sm" 
                                className="h-7 w-7 p-0 bg-green-600 hover:bg-green-700"
                                onClick={() => updateCardParking(aircraft.registration, cardParkingValue, aircraft.aircraft_type)}
                                disabled={savingCardParking || !cardParkingValue}
                              >
                                {savingCardParking ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />}
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="h-7 w-7 p-0"
                                onClick={() => { setEditingCardParking(null); setCardParkingValue(""); }}
                              >
                                <X size={12} />
                              </Button>
                            </div>
                          ) : !isManual ? (
                            <Badge 
                              variant="outline" 
                              className="text-xs cursor-pointer hover:bg-[#d4af37]/10"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingCardParking(aircraft.registration);
                                setCardParkingValue(aircraft.parking_position || "");
                              }}
                            >
                              <MapPin size={10} className="mr-1" />
                              {aircraft.parking_position || "TBD"}
                              <Edit2 size={10} className="ml-1 opacity-50" />
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs border-violet-400 text-violet-600">
                              <MapPin size={10} className="mr-1" />
                              {aircraft.parking_position}
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-[#64748b] dark:text-gray-400 space-y-1">
                          <div>{aircraft.aircraft_type}{isManual && aircraft.operator ? ` — ${aircraft.operator}` : ""}</div>
                          {isManual ? (
                            <>
                              {aircraft.arrival_time && (
                                <div className="flex items-center gap-1">
                                  <PlaneLanding size={12} className="text-violet-500" />
                                  Arr: {aircraft.arrival_time}
                                </div>
                              )}
                              {aircraft.departure ? (
                                <div className="flex items-center gap-1">
                                  <PlaneTakeoff size={12} className="text-violet-500" />
                                  Dep: {aircraft.departure.flight_date} {aircraft.departure.time}
                                </div>
                              ) : (
                                <div className="text-violet-500 font-medium">
                                  No departure set — delete manually
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              <div className="flex items-center gap-1">
                                <PlaneLanding size={12} />
                                {aircraft.arrival_date} {aircraft.arrival_time}
                              </div>
                              {aircraft.departure ? (
                                <div className="flex items-center gap-1">
                                  <PlaneTakeoff size={12} />
                                  {aircraft.departure.flight_date} {aircraft.departure.time}
                                </div>
                              ) : (
                                <div className="text-yellow-600 dark:text-yellow-400 font-medium">
                                  Long Term Parking
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })
                  )
                ) : null}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Stand Closure Scheduler - All users can manage */}
      <StandClosureScheduler onClosureChange={fetchData} stands={Object.keys(standPositions)} />

      {/* Operational Restriction Scheduler - All users can manage */}
      <div className="mt-6">
        <OperationalRestrictionScheduler onRestrictionChange={fetchData} />
      </div>

      {/* Selected Aircraft Details */}
      {selectedAircraft && (
        <Card className="bg-white dark:bg-[#1e293b]">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Info size={20} className="text-[#d4af37]" />
                Aircraft Details: {selectedAircraft.registration}
              </span>
              <Button variant="ghost" size="sm" onClick={() => setSelectedAircraft(null)}>
                Close
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs text-[#64748b]">Aircraft Type</Label>
                <p className="font-medium">{selectedAircraft.aircraft_type}</p>
              </div>
              <div>
                <Label className="text-xs text-[#64748b]">Operator</Label>
                <p className="font-medium">{selectedAircraft.operator || "Unknown"}</p>
              </div>
              <div>
                <Label className="text-xs text-[#64748b]">Current Parking</Label>
                <p className="font-medium">{selectedAircraft.parking_position || "Unassigned"}</p>
              </div>
              <div>
                <Label className="text-xs text-[#64748b]">Status</Label>
                <Badge style={{ backgroundColor: getAircraftColor(selectedAircraft).bg, color: "#fff" }}>
                  {selectedAircraft.parking_status}
                </Badge>
              </div>
              <div>
                <Label className="text-xs text-[#64748b]">Arrived From</Label>
                <p className="font-medium">{selectedAircraft.arrival_from} on {selectedAircraft.arrival_date}</p>
              </div>
              <div>
                <Label className="text-xs text-[#64748b]">Dimensions</Label>
                <p className="font-medium text-sm">
                  W: {selectedAircraft.dimensions?.wingspan?.toFixed(1)}m | 
                  L: {selectedAircraft.dimensions?.length?.toFixed(1)}m | 
                  H: {selectedAircraft.dimensions?.height?.toFixed(1)}m
                </p>
              </div>
              <div>
                <Label className="text-xs text-[#64748b]">MTOW</Label>
                <p className="font-medium">{selectedAircraft.mtow} kg</p>
              </div>
              {selectedAircraft.departure ? (
                <div>
                  <Label className="text-xs text-[#64748b]">Departure</Label>
                  <p className="font-medium">
                    {selectedAircraft.departure.flight_date} {selectedAircraft.departure.time} → {selectedAircraft.departure.destination}
                  </p>
                </div>
              ) : (
                <div>
                  <Label className="text-xs text-[#64748b]">Departure</Label>
                  <p className="font-medium text-yellow-600">No departure scheduled</p>
                </div>
              )}
            </div>
            
            {/* Quick parking assignment */}
            <div className="mt-4 pt-4 border-t">
              <Label className="text-sm font-medium mb-2 block">Assign Parking Stand</Label>
              <div className="flex flex-wrap gap-2">
                {Object.keys(standPositions).slice(0, 20).map((standId) => {
                  const isOccupied = aircraftOnGround.some(
                    a => a.parking_position?.toUpperCase() === standId && a.registration !== selectedAircraft.registration
                  );
                  const isCurrent = selectedAircraft.parking_position?.toUpperCase() === standId;
                  
                  return (
                    <Button
                      key={standId}
                      variant={isCurrent ? "default" : "outline"}
                      size="sm"
                      disabled={isOccupied || updateLoading}
                      className={`${isCurrent ? "bg-[#d4af37] text-white" : ""} ${isOccupied ? "opacity-50" : ""}`}
                      onClick={() => handleStandDrop(standId)}
                    >
                      {standId}
                    </Button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Warning Dialog */}
      <Dialog open={showWarningDialog} onOpenChange={setShowWarningDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-600">
              <AlertTriangle size={20} />
              Stand Compatibility Warning
            </DialogTitle>
          </DialogHeader>
          {warningData && (
            <div className="space-y-4">
              <p>
                Assigning <strong>{warningData.aircraft.registration}</strong> ({warningData.aircraft.aircraft_type}) 
                to stand <strong>{warningData.stand}</strong>:
              </p>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg">
                <ul className="list-disc list-inside space-y-1 text-sm">
                  {warningData.warnings.map((warning, i) => (
                    <li key={i} className="text-yellow-700 dark:text-yellow-300">{warning}</li>
                  ))}
                </ul>
              </div>
              <div className="text-sm text-[#64748b]">
                <strong>Aircraft Dimensions:</strong>
                <br />
                Wingspan: {warningData.dimensions?.wingspan?.toFixed(1)}m | 
                Length: {warningData.dimensions?.length?.toFixed(1)}m | 
                Height: {warningData.dimensions?.height?.toFixed(1)}m
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWarningDialog(false)}>
              Cancel
            </Button>
            {warningData?.allowOverride && (
              <Button 
                onClick={confirmParkingWithWarnings}
                disabled={updateLoading}
                className="bg-yellow-600 hover:bg-yellow-700"
              >
                {updateLoading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                Assign Anyway
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rotation Dialog */}
      <Dialog open={showRotationDialog} onOpenChange={setShowRotationDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col bg-white dark:bg-[#1e293b] border-2 border-[#e2e8f0] dark:border-[#334155] shadow-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCw size={20} className="text-[#d4af37]" />
              Aircraft Rotation: {rotationData?.registration}
            </DialogTitle>
          </DialogHeader>
          
          {rotationLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin mr-2" size={24} />
              Loading rotation data...
            </div>
          ) : rotationData ? (
            <ScrollArea className="flex-1 pr-4">
              <div className="space-y-6">
                {/* Aircraft Info */}
                {rotationData.aircraft_info && (
                  <div className="bg-[#f8fafc] dark:bg-[#0f172a] p-4 rounded-lg">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <Plane size={16} className="text-[#d4af37]" />
                      Aircraft Information
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <span className="text-[#64748b]">Type:</span>
                        <span className="ml-1 font-medium">{rotationData.aircraft_info.aircraft_type}</span>
                      </div>
                      <div>
                        <span className="text-[#64748b]">Operator:</span>
                        <span className="ml-1 font-medium">{rotationData.aircraft_info.operator}</span>
                      </div>
                      <div>
                        <span className="text-[#64748b]">MTOW:</span>
                        <span className="ml-1 font-medium">{rotationData.aircraft_info.mtow} kg</span>
                      </div>
                      <div>
                        <span className="text-[#64748b]">Dimensions:</span>
                        <span className="ml-1 font-medium text-xs">
                          W:{rotationData.aircraft_info.wingspan?.toFixed(1) || "N/A"}m 
                          L:{rotationData.aircraft_info.length?.toFixed(1) || "N/A"}m 
                          H:{rotationData.aircraft_info.height?.toFixed(1) || "N/A"}m
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Last Arrival (Inbound) */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2 text-green-600">
                    <PlaneLanding size={16} />
                    Last Inbound Flight
                    {rotationData.total_arrivals > 0 && (
                      <Badge variant="outline" className="text-xs ml-2">
                        {rotationData.total_arrivals} total
                      </Badge>
                    )}
                  </h4>
                  {!rotationData.last_arrival ? (
                    <p className="text-[#64748b] text-sm">No arrivals found</p>
                  ) : (
                    <div className="p-3 border rounded-lg bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-green-600">{rotationData.last_arrival.flight_number || "N/A"}</Badge>
                          <span className="font-medium">{rotationData.last_arrival.flight_date}</span>
                          <span className="text-[#64748b]">{rotationData.last_arrival.sta || rotationData.last_arrival.ata || rotationData.last_arrival.time}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-[#64748b]">From:</span>
                          <Badge variant="outline">{rotationData.last_arrival.origin}</Badge>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <MapPin size={14} className="text-[#64748b]" />
                          {editingFlightId === rotationData.last_arrival.id ? (
                            <div className="flex items-center gap-2">
                              <Select
                                value={editingParking}
                                onValueChange={setEditingParking}
                              >
                                <SelectTrigger className="w-24 h-8">
                                  <SelectValue placeholder="Stand" />
                                </SelectTrigger>
                                <SelectContent>
                                  {Object.keys(standPositions).sort().map((standId) => {
                                    const status = standAvailability[standId];
                                    const isOccupied = status?.state === 'occupied';
                                    const isClosed = status?.state === 'closed';
                                    const isUnavailable = isOccupied || isClosed;
                                    const occupantReg = status?.aircraft?.registration;
                                    return (
                                      <SelectItem key={standId} value={standId} disabled={isUnavailable}
                                        className={isUnavailable ? "opacity-40 line-through text-red-400" : ""}
                                      >
                                        {standId}{isOccupied ? ` (${occupantReg})` : isClosed ? " (closed)" : ""}
                                      </SelectItem>
                                    );
                                  })}
                                </SelectContent>
                              </Select>
                              <Button 
                                size="sm" 
                                className="h-8 bg-green-600 hover:bg-green-700"
                                onClick={() => updateFlightParking(rotationData.last_arrival.id, editingParking, rotationData.aircraft_type)}
                                disabled={savingParking || !editingParking}
                              >
                                {savingParking ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="h-8"
                                onClick={() => { setEditingFlightId(null); setEditingParking(""); }}
                              >
                                <X size={14} />
                              </Button>
                            </div>
                          ) : (
                            <>
                              <span className="font-medium">{rotationData.last_arrival.parking_position || "Not assigned"}</span>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="h-6 px-2"
                                onClick={() => { 
                                  setEditingFlightId(rotationData.last_arrival.id); 
                                  setEditingParking(rotationData.last_arrival.parking_position || ""); 
                                }}
                              >
                                <Edit2 size={12} />
                              </Button>
                            </>
                          )}
                        </div>
                        {rotationData.last_arrival.handling_agent && (
                          <Badge variant="outline" className="text-xs">{rotationData.last_arrival.handling_agent}</Badge>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Arrow between flights */}
                <div className="flex justify-center py-2">
                  <ArrowRight size={24} className="text-[#d4af37] rotate-90" />
                </div>

                {/* Last Departure (Outbound) */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2 text-blue-600">
                    <PlaneTakeoff size={16} />
                    Last Outbound Flight
                    {rotationData.total_departures > 0 && (
                      <Badge variant="outline" className="text-xs ml-2">
                        {rotationData.total_departures} total
                      </Badge>
                    )}
                  </h4>
                  {!rotationData.last_departure ? (
                    <p className="text-[#64748b] text-sm">No departures found</p>
                  ) : (
                    <div className="p-3 border rounded-lg bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-blue-600">{rotationData.last_departure.flight_number || "N/A"}</Badge>
                          <span className="font-medium">{rotationData.last_departure.flight_date}</span>
                          <span className="text-[#64748b]">{rotationData.last_departure.std || rotationData.last_departure.atd || rotationData.last_departure.time}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-[#64748b]">To:</span>
                          <Badge variant="outline">{rotationData.last_departure.destination}</Badge>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <MapPin size={14} className="text-[#64748b]" />
                          {editingFlightId === rotationData.last_departure.id ? (
                            <div className="flex items-center gap-2">
                              <Select
                                value={editingParking}
                                onValueChange={setEditingParking}
                              >
                                <SelectTrigger className="w-24 h-8">
                                  <SelectValue placeholder="Stand" />
                                </SelectTrigger>
                                <SelectContent>
                                  {Object.keys(standPositions).sort().map((standId) => {
                                    const status = standAvailability[standId];
                                    const isOccupied = status?.state === 'occupied';
                                    const isClosed = status?.state === 'closed';
                                    const isUnavailable = isOccupied || isClosed;
                                    const occupantReg = status?.aircraft?.registration;
                                    return (
                                      <SelectItem key={standId} value={standId} disabled={isUnavailable}
                                        className={isUnavailable ? "opacity-40 line-through text-red-400" : ""}
                                      >
                                        {standId}{isOccupied ? ` (${occupantReg})` : isClosed ? " (closed)" : ""}
                                      </SelectItem>
                                    );
                                  })}
                                </SelectContent>
                              </Select>
                              <Button 
                                size="sm" 
                                className="h-8 bg-blue-600 hover:bg-blue-700"
                                onClick={() => updateFlightParking(rotationData.last_departure.id, editingParking, rotationData.aircraft_type)}
                                disabled={savingParking || !editingParking}
                              >
                                {savingParking ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="h-8"
                                onClick={() => { setEditingFlightId(null); setEditingParking(""); }}
                              >
                                <X size={14} />
                              </Button>
                            </div>
                          ) : (
                            <>
                              <span className="font-medium">{rotationData.last_departure.parking_position || "Not assigned"}</span>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="h-6 px-2"
                                onClick={() => { 
                                  setEditingFlightId(rotationData.last_departure.id); 
                                  setEditingParking(rotationData.last_departure.parking_position || ""); 
                                }}
                              >
                                <Edit2 size={12} />
                              </Button>
                            </>
                          )}
                        </div>
                        {rotationData.last_departure.handling_agent && (
                          <Badge variant="outline" className="text-xs">{rotationData.last_departure.handling_agent}</Badge>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </ScrollArea>
          ) : (
            <p className="text-center text-[#64748b] py-8">No rotation data available</p>
          )}
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowRotationDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit External (Manual) Aircraft Dialog */}
      <Dialog open={!!editManualAircraft} onOpenChange={(o) => { if (!o) setEditManualAircraft(null); }}>
        <DialogContent className="bg-white dark:bg-[#1e293b] max-w-md" data-testid="edit-manual-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0f172a] dark:text-white">
              <div className="w-2.5 h-2.5 rounded-full bg-violet-500" />
              Edit External Aircraft
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-[#64748b]">Registration</Label>
              <Input
                value={editManualForm.registration || ""}
                onChange={(e) => setEditManualForm({ ...editManualForm, registration: e.target.value.toUpperCase() })}
                className="h-9 uppercase"
                data-testid="edit-manual-reg-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#64748b]">Stand</Label>
                <Input
                  value={editManualForm.stand || ""}
                  onChange={(e) => setEditManualForm({ ...editManualForm, stand: e.target.value.toUpperCase() })}
                  className="h-9 uppercase"
                  data-testid="edit-manual-stand-input"
                />
              </div>
              <div>
                <Label className="text-xs text-[#64748b]">Aircraft Type</Label>
                <Input
                  value={editManualForm.aircraft_type || ""}
                  onChange={(e) => setEditManualForm({ ...editManualForm, aircraft_type: e.target.value })}
                  className="h-9"
                  data-testid="edit-manual-type-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#64748b]">Operator</Label>
              <Input
                value={editManualForm.operator || ""}
                onChange={(e) => setEditManualForm({ ...editManualForm, operator: e.target.value })}
                className="h-9"
                data-testid="edit-manual-operator-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#64748b]">Arrival</Label>
                <Input
                  type="datetime-local"
                  value={editManualForm.arrival_time || ""}
                  onChange={(e) => setEditManualForm({ ...editManualForm, arrival_time: e.target.value })}
                  className="h-9 text-xs"
                  data-testid="edit-manual-arrival-input"
                />
              </div>
              <div>
                <Label className="text-xs text-[#64748b]">Departure</Label>
                <Input
                  type="datetime-local"
                  value={editManualForm.departure_time || ""}
                  onChange={(e) => setEditManualForm({ ...editManualForm, departure_time: e.target.value })}
                  className="h-9 text-xs"
                  data-testid="edit-manual-departure-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditManualAircraft(null)}>
              <X size={16} className="mr-1" /> Cancel
            </Button>
            <Button
              onClick={handleSaveEditManual}
              disabled={savingEditManual || !editManualForm.registration || !editManualForm.stand}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="save-manual-edit-btn"
            >
              {savingEditManual ? <Loader2 size={16} className="animate-spin mr-1" /> : <Save size={16} className="mr-1" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
